import { ChevronLeftIcon } from '@heroicons/react/16/solid'
import { useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button } from '../catalyst/button'
import { ErrorMessage, Field as FormField, FieldGroup, Label } from '../catalyst/fieldset'
import { Heading } from '../catalyst/heading'
import { Input } from '../catalyst/input'
import { Link } from '../catalyst/link'
import { RadioCards } from '../catalyst/radio-cards'
import { Text } from '../catalyst/text'
import { useAuth } from '../lib/auth'
import { formErrors, noErrors, type FormErrors } from '../lib/form-errors'
import { useFarms } from '../lib/farms'
import { recordStockEntry, STOCK_ENTRY_LABEL, stockPath, useStockItem } from '../lib/inventory'
import { draftCanonical, emptyAmount, parseAmount, StockAmountInput } from '../lib/stock-amount-input'
import { formatAmount, packageBreakdown } from '../lib/stock-units'
import type { StockEntryKind, StockItem } from '../lib/types'
import { todayIso } from '../wizard/flow'
import { Shell } from './shell'

// Recording one stock entry: a delivery (Received), use (Used) or a count of what is on hand. A count
// is saved as the difference from what the app thinks is on hand, so this page shows that difference.

const KINDS: StockEntryKind[] = ['RECEIVED', 'USED', 'COUNTED']

function EntryForm({ item, timezone, initialKind, onDone }: { item: StockItem; timezone?: string; initialKind: StockEntryKind; onDone: () => void }) {
  const { org } = useAuth()
  const [kind, setKind] = useState<StockEntryKind>(initialKind)
  const [amount, setAmount] = useState(() => emptyAmount(item.package?.unit ?? item.shownIn)) // next to packages, a loose amount is usually in the package's unit
  const [occurredOn, setOccurredOn] = useState(() => todayIso(timezone))
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<FormErrors>(noErrors)

  const invalid = (key: string) => key in errors.fields
  const message = (key: string) => (invalid(key) ? <ErrorMessage>{errors.fields[key]}</ErrorMessage> : null)
  const typed = draftCanonical(item, amount)
  const change = typed === null ? null : Math.round((kind === 'COUNTED' ? typed - item.onHand : kind === 'USED' ? -typed : typed) * 1e6) / 1e6

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    const quantity = parseAmount(amount, !!item.package)
    if (!quantity || !org) return
    setSubmitting(true)
    setErrors(noErrors)
    try {
      await recordStockEntry(org.id, item.farmId, item.id, { kind, quantity, occurredOn, note: note.trim() || null })
      onDone()
    } catch (err) {
      setErrors(formErrors(err, 'Could not record it.'))
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mt-8">
      {errors.general && <Text className="mb-6 text-red-600 dark:text-red-400">{errors.general}</Text>}
      <FieldGroup>
        <RadioCards<StockEntryKind> label="What happened?" layout="wrap" value={kind} options={KINDS.map((k) => ({ value: k, label: STOCK_ENTRY_LABEL[k].title }))} onChange={setKind} />

        <StockAmountInput
          label={STOCK_ENTRY_LABEL[kind].verb}
          description={kind === 'COUNTED' ? `On hand now: ${formatAmount(item, item.onHand)}${packageBreakdown(item) ? ` (${packageBreakdown(item)})` : ''}.` : undefined}
          dimension={item.dimension}
          pkg={item.package}
          draft={amount}
          onChange={setAmount}
          invalid={invalid('quantity') || Object.keys(errors.fields).some((k) => k.startsWith('quantity.'))}
          required
          autoFocus
        />
        {message('quantity') ?? message('quantity.packages') ?? message('quantity.unit')}

        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
          <FormField>
            <Label>Date</Label>
            <Input type="date" value={occurredOn} max={todayIso(timezone)} onChange={(e) => setOccurredOn(e.target.value)} invalid={invalid('occurredOn')} required />
            {message('occurredOn')}
          </FormField>
        </div>

        <FormField>
          <Label>Note</Label>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            invalid={invalid('note')}
            placeholder={kind === 'RECEIVED' ? 'e.g. Co-op delivery, invoice 4471' : kind === 'USED' ? 'e.g. Bloom spray, Field A' : 'e.g. One bag torn'}
          />
          {message('note')}
        </FormField>

        {change !== null && (
          <Text>
            {change === 0
              ? 'This matches what is on hand: there is nothing to record.'
              : `On hand goes from ${formatAmount(item, item.onHand)} to ${formatAmount(item, item.onHand + change)}.`}
          </Text>
        )}
      </FieldGroup>

      <div className="sticky bottom-0 -mx-6 mt-10 flex justify-end gap-3 border-t border-zinc-950/10 bg-white px-6 py-4 sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 dark:border-white/10 dark:bg-zinc-900 sm:dark:bg-transparent">
        <Button plain onClick={() => onDone()}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting || change === 0}>
          {submitting ? 'Saving…' : 'Record'}
        </Button>
      </div>
    </form>
  )
}

export function StockEntryPage() {
  const { farmId, stockItemId } = useParams<{ farmId: string; stockItemId: string }>()
  const [params] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { org } = useAuth()
  const { getFarm, loading: farmsLoading } = useFarms()
  const { item, loading, error } = useStockItem(org?.id, farmId, stockItemId)
  const canWrite = org?.role !== 'AGRONOMIST'

  const farm = farmId ? getFarm(farmId) : undefined
  if (!farmsLoading && !farm) return <Navigate to="/" replace />
  if (!farm || !org || !farmId || !stockItemId) return null

  const back = stockPath(farmId, stockItemId)
  if (!canWrite) return <Navigate to={back} replace />
  const initialKind = KINDS.find((k) => k === params.get('kind')) ?? 'RECEIVED'

  // Back to the item's page (which reloads its on-hand), without leaving this form in history.
  const done = () => (location.key !== 'default' ? navigate(-1) : navigate(back, { replace: true }))

  return (
    <Shell>
      <Link href={back} className="inline-flex items-center gap-1 text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
        <ChevronLeftIcon className="size-4" />
        {item?.name ?? 'Back'}
      </Link>

      <div className="mx-auto max-w-2xl">
        {loading ? (
          <Text className="mt-6">Loading…</Text>
        ) : !item || item.archivedAt ? (
          <div className="mt-6 rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
            <Text>{error ?? (item?.archivedAt ? 'This has been archived and takes no new entries.' : 'Not found.')}</Text>
          </div>
        ) : (
          <>
            <Heading className="mt-4">{item.name}</Heading>
            <Text>
              On hand: {formatAmount(item, item.onHand)}
              {packageBreakdown(item) && ` (${packageBreakdown(item)})`}
            </Text>
            <EntryForm item={item} timezone={farm.timezone} initialKind={initialKind} onDone={done} />
          </>
        )}
      </div>
    </Shell>
  )
}
